import {SelectProvider, useSelectContext} from '@ariakit/react'
import {Flexbox} from 'src/components/flexbox/Flexbox'
import {SelectList, SelectPopover, SelectItem, SelectTrigger} from 'src/components/select-atoms/SelectAtoms'
import {useAdminHostApprovalTaskRooms} from 'src/features/admin-host-approval/services/useAdminHostApprovalTaskRooms'
import {Spinner} from 'src/components/spinner/Spinner'
import {InputHelpText} from 'src/components/input-help-text/InputHelpText'
import {useTranslation} from 'react-i18next'
import {AdminHostApprovalTaskImage, ROOM_ACCESSIBILITY_FEATURES_MAP} from 'src/features/admin-host-approval/types'
import {useUpdateHostApprovalTaskImage} from 'src/features/admin-host-approval/services/useUpdateHostApprovalTaskImage'
import {useAccessibilityFeatures} from 'src/features/admin-host-approval/services/useAccessibilityFeatures'
import {raise} from 'src/helpers/helpers'
import {useCallback} from 'react'

export const AdminHostApprovalImageRoomSelector = ({
    taskId,
    imageId,
    roomId,
    amenityId
}: {
    taskId: number
    imageId: number
    roomId: AdminHostApprovalTaskImage['room_id']
    amenityId: AdminHostApprovalTaskImage['amenity_id']
}) => {
    const {t} = useTranslation()
    const {data: rooms, isLoading, isError} = useAdminHostApprovalTaskRooms({staleTime: Infinity})
    const {data: accessibilityFeatures} = useAccessibilityFeatures({staleTime: Infinity})
    const {mutate: updateImage} = useUpdateHostApprovalTaskImage({taskId, imageId})

    const handleRoomChange = useCallback(
        (newRoomId: string) => {
            const newRoom = rooms?.find(room => room.id === +newRoomId)
            if (!newRoom || !accessibilityFeatures || !amenityId) {
                updateImage({room_id: +newRoomId})
                return
            }

            const currentAmenity = accessibilityFeatures.find(f => f.id === amenityId)
            if (!currentAmenity) {
                updateImage({room_id: +newRoomId})
                return
            }

            const allowedFeatureKeys = ROOM_ACCESSIBILITY_FEATURES_MAP[newRoom.internal_name]
            const isAmenityValidForNewRoom = allowedFeatureKeys.includes(currentAmenity.internal_key)

            if (isAmenityValidForNewRoom) {
                updateImage({room_id: +newRoomId})
            } else {
                updateImage({room_id: +newRoomId, amenity_id: null})
            }
        },
        [rooms, accessibilityFeatures, amenityId, updateImage]
    )

    return (
        <SelectProvider defaultValue={roomId?.toString() ?? ''} setValue={handleRoomChange}>
            <Flexbox direction="column" gap={2} fullWidth>
                <AdminHostApprovalImageRoomSelectorTrigger />
                <SelectPopover portal>
                    {isLoading ? (
                        <Flexbox justify="center">
                            <Spinner size={20} />
                        </Flexbox>
                    ) : isError ? (
                        <Flexbox justify="center">
                            <InputHelpText error={t('errors:default')} />
                        </Flexbox>
                    ) : (
                        <SelectList>
                            {rooms.map(room => (
                                <SelectItem key={room.id} value={room.id.toString()}>
                                    {room.name}
                                </SelectItem>
                            ))}
                        </SelectList>
                    )}
                </SelectPopover>
            </Flexbox>
        </SelectProvider>
    )
}

const AdminHostApprovalImageRoomSelectorTrigger = () => {
    const {t} = useTranslation()
    const {data: rooms} = useAdminHostApprovalTaskRooms({enabled: false})
    const selectContext = useSelectContext() ?? raise('Select must be a child of SelectCombobox')
    const selectValue = selectContext.useState('value') as string //as needed because it's a single select and the provided type is string | string[]

    return (
        <SelectTrigger
            placeholder={t('host_approval_task:select_room_placeholder')}
            fullWidth
            value={selectValue}
            displayValue={value => rooms?.find(room => room.id.toString() == value)?.name}
        />
    )
}
